import React, { useState, useEffect } from 'react'
import { Spin, Typography } from 'antd'
import { DownloadOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import InputAdditionalContacts from '../../input-additional-contacts'
import InputPhoneClient from '../../input-phone-client'
import fetchAPI from '../../../../lib/utils/fetch-api'
import { formatWithDisplayPattern, parseDisplayFormatValue } from '../../input-text/mask-utils'
import { formatPhoneNumber } from '../../../../lib/utils/helpers'

const { Text } = Typography

interface DisabledFieldRendererProps {
  value: any
  schema: any
  UISchema?: any
  formData?: any
}

export function DisabledFieldRenderer({ value, schema, UISchema }: DisabledFieldRendererProps) {
  const style = schema?.disabled_style || {}
  const [asyncLabel, setAsyncLabel] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (schema?.view?.name === 'select-from-enum-async' && value && schema?.options?.apiUrl) {
      setLoading(true)
      const apiUrl = schema.options.apiUrl
      const field = schema.options.field

      const params = new URLSearchParams()

      params.set('selected', String(value))
      params.set('field', field)

      const url = `${apiUrl.split('?')[0]}?${params.toString()}`

      fetchAPI(url)
        .then((response: any) => {
          if (response?.selectedOption?.label) {
            setAsyncLabel(response.selectedOption.label)
          } else if (response?.selectedOption?.title) {
            setAsyncLabel(response.selectedOption.title)
          } else {
            setAsyncLabel(String(value))
          }
        })
        .catch(() => {
          setAsyncLabel(String(value))
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [schema?.view?.name, value, schema?.options?.apiUrl])

  function getEnumTitle(localSchema: any, localValue: any): string | null {
    const oneOf: any[] = Array.isArray(localSchema?.oneOf) ? localSchema.oneOf : []
    if (oneOf.length) {
      const item = oneOf.find(item => item?.const === localValue)
      if (item && (item.title || item.name)) return item.title || item.name
    }
    const enumValues: any[] = Array.isArray(localSchema?.enum) ? localSchema.enum : []
    if (enumValues.length) {
      return String(localValue)
    }
    return null
  }

  function formatBySchema(localSchema: any, localValue: any): string | null {
    if (localValue === null || localValue === undefined) return null
    if (typeof localValue !== 'string') return null
    const fmt = localSchema?.format || localSchema?.input?.format
    if (fmt === 'date') {
      const d = dayjs(localValue)
      return d.isValid() ? d.format('DD.MM.YYYY') : localValue
    }
    if (fmt === 'date-time') {
      const d = dayjs(localValue)
      return d.isValid() ? d.format('DD.MM.YYYY HH:mm') : localValue
    }
    if (fmt === 'time') {
      const d = dayjs(localValue)
      return d.isValid() ? d.format('HH:mm') : localValue
    }
    return null
  }

  if (schema?.view?.name === 'input-shipping-date') {
    const d = dayjs(value)
    const formatted = d.isValid() ? d.format('DD.MM.YYYY') : value
    return <Text style={style}>{formatted}</Text>
  }

  if (schema?.format === 'file-s3' || schema?.view?.name === 'file-s3') {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return <Text type="secondary">Файл не загружен</Text>
    }

    const files = Array.isArray(value) ? value : [value]

    return (
      <div>
        {files.map((file: any, index: number) => {
          if (!file || !file.url) {
            return (
              <Text key={index} type="secondary" style={style}>
                Файл недоступен
              </Text>
            )
          }

          return (
            <div key={index} style={{ marginBottom: 4 }}>
              <a
                href={file.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  textDecoration: 'none',
                }}
              >
                <DownloadOutlined />
                <Text style={style}>{file.name || file.filename || 'Скачать файл'}</Text>
              </a>
            </div>
          )
        })}
      </div>
    )
  }

  if (!value && value !== 0) {
    return (
      <Text type="secondary" style={style}>
        Не указано
      </Text>
    )
  }

  if (schema?.format === 'phone' && typeof value === 'string') {
    return <Text style={style}>{formatPhoneNumber(value)}</Text>
  }

  if (schema?.format === 'phones') {
    let phones: string[] = []
    if (Array.isArray(value)) {
      phones = value.filter(Boolean)
    } else if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value)
        phones = Array.isArray(parsed) ? parsed.filter(Boolean) : []
      } catch {
        phones = value ? [value] : []
      }
    }
    if (phones.length === 0) {
      return (
        <Text type="secondary" style={style}>
          Не указано
        </Text>
      )
    }
    const formatted = phones.map((p: string) => formatPhoneNumber(p))
    return <Text style={{ ...style, whiteSpace: 'pre-line' }}>{formatted.join('\n')}</Text>
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return (
        <Text type="secondary" style={style}>
          Не указано
        </Text>
      )
    }
    const mapped = value.map(item => {
      const enumTitle = getEnumTitle(schema, item)
      if (enumTitle !== null) return enumTitle
      const formatted = formatBySchema(schema, item)
      if (formatted !== null) return formatted
      return String(item)
    })
    return <Text style={style}>{mapped.join(', ')}</Text>
  }
  if (schema?.format === 'phone-client' && schema?.type === 'string') {
    return <InputPhoneClient UISchema={UISchema} value={value} />
  }
  if (typeof value === 'object' && value !== null) {
    if (value.title || value.name) {
      return <Text style={style}>{value.title || value.name}</Text>
    }
    if (schema?.format === 'additional-contacts') {
      return <InputAdditionalContacts value={value} />
    }
    return <Text style={style}>{JSON.stringify(value)}</Text>
  }

  const enumTitle = getEnumTitle(schema, value)
  if (enumTitle !== null) {
    return <Text style={style}>{enumTitle}</Text>
  }

  const formattedBySchema = formatBySchema(schema, value)
  if (formattedBySchema !== null) {
    return <Text style={style}>{formattedBySchema}</Text>
  }

  if (schema?.view?.name === 'select-from-enum-async') {
    if (loading) {
      return <Spin size="small" />
    }
    if (asyncLabel) {
      return <Text style={style}>{asyncLabel}</Text>
    }
    return <Text style={style}>{String(value)}</Text>
  }

  if (typeof value === 'boolean') {
    return <Text style={style}>{value ? 'Да' : 'Нет'}</Text>
  }

  if (typeof value === 'number') {
    return <Text style={style}>{value.toLocaleString()}</Text>
  }

  if (typeof value === 'string') {
    if (value.trim() === '') {
      return (
        <Text type="secondary" style={style}>
          Не указано
        </Text>
      )
    }
    const displayFormat = schema?.options?.displayFormat ?? schema?.input?.options?.displayFormat
    if (displayFormat) {
      const raw = parseDisplayFormatValue(value, displayFormat)
      const formatted = formatWithDisplayPattern(raw, displayFormat)
      return <Text style={style}>{formatted}</Text>
    }
    return <Text style={style}>{value}</Text>
  }

  if (value === null || value === undefined) {
    return (
      <Text type="secondary" style={style}>
        Не указано
      </Text>
    )
  }

  return <Text style={style}>{String(value)}</Text>
}
